import api from './api';

const unwrap = (response) => {
  if (response?.data?.data !== undefined) return response.data.data;
  return response?.data ?? response;
};

const requestWithToast = async (promise, fallbackMessage = 'Request failed') => {
  try {
    const response = await promise;
    return unwrap(response);
  } catch (error) {
    const message =
      error?.response?.data?.message || error?.message || fallbackMessage;
    throw new Error(message);
  }
};

const verificationRequestService = {
  
  // Initialize service
  initialize: () => {},

  // Create new verification request
  createVerificationRequest: async (requestData) => {
    return await requestWithToast(
      api.post('/verification-requests', requestData),
      'Failed to create verification request'
    );
  },

  // Get verification request by ID
  getVerificationRequest: async (requestId) => {
    return await requestWithToast(
      api.get(`/verification-requests/${requestId}`),
      'Failed to load verification request'
    );
  },

  // Get verification requests by filters
  getVerificationRequests: async (filters = {}) => {
    return await requestWithToast(
      api.get('/verification-requests', { params: filters }),
      'Failed to load verification requests'
    );
  },

  // Assign verifier to request
  assignVerifier: async (requestId, verifierId, assignmentData = {}) => {
    return await requestWithToast(
      api.post(`/verification-requests/${requestId}/assign`, { verifierId, ...assignmentData }),
      'Failed to assign verifier'
    );
  },

  // Accept verification assignment
  acceptAssignment: async (requestId, verifierId, acceptanceData = {}) => {
    return await requestWithToast(
      api.post(`/verification-requests/${requestId}/accept`, { verifierId, ...acceptanceData }),
      'Failed to accept assignment'
    );
  },

  // Start verification (check-in)
  startVerification: async (requestId, verifierId, locationData = null) => {
    return await requestWithToast(
      api.post(`/verification-requests/${requestId}/start`, { verifierId, locationData }),
      'Failed to start verification'
    );
  },

  // Complete verification
  completeVerification: async (requestId, verifierId, verificationData) => {
    return await requestWithToast(
      api.post(`/verification-requests/${requestId}/complete`, { verifierId, ...verificationData }),
      'Failed to complete verification'
    );
  },

  // Update approval status
  updateApprovalStatus: async (requestId, approvalData, approverRole) => {
    return await requestWithToast(
      api.post(`/verification-requests/${requestId}/approval`, { approverRole, ...approvalData }),
      'Failed to update approval'
    );
  },

  // Add message to communication thread
  addMessage: async (requestId, messageData) => {
    return await requestWithToast(
      api.post(`/verification-requests/${requestId}/messages`, messageData),
      'Failed to send message'
    );
  },

  // Get verification requests dashboard data
  getDashboardData: async (userId, userRole) => {
    return await requestWithToast(
      api.get('/verification-requests/dashboard', { params: { userId, userRole } }),
      'Failed to load verification dashboard'
    );
  },

  // Generate verification report
  generateVerificationReport: async (filters = {}) => {
    const requests = await verificationRequestService.getVerificationRequests(filters);

    const statusBreakdown = requests.reduce((acc, request) => {
      const key = request.status || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const priorityBreakdown = requests.reduce((acc, request) => {
      const key = request.priority || 'normal';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const averageScore = (() => {
      const scored = requests.filter(r => r?.verification?.verificationScore != null);
      if (!scored.length) return 0;
      const total = scored.reduce((sum, r) => sum + (r.verification.verificationScore || 0), 0);
      return Math.round((total / scored.length) * 10) / 10;
    })();

    const totalFees = requests.reduce((sum, request) => sum + (request.payment?.verificationFee || 0), 0);
    const completedFees = requests
      .filter(request => request.payment?.paymentStatus === 'completed')
      .reduce((sum, request) => sum + (request.payment?.verificationFee || 0), 0);

    return {
      generatedAt: new Date().toISOString(),
      filters,
      totals: {
        requests: requests.length,
        completedFees,
        totalFees
      },
      statusBreakdown,
      priorityBreakdown,
      averageScore,
      requests
    };
  }
};

export default verificationRequestService;